import AsyncStorage from "@react-native-async-storage/async-storage";
import { useCallback } from "react";

import {
  getUserId,
  getOrganizationId,
  generateSessionId,
} from "../utils/authUtils";

import { env } from "@/lib/env";
import type { AuthUserFragment } from "@/generated/graphql";
import {
  currentUserVar,
  userIdVar,
  organizationIdVar,
  sessionIdVar,
} from "@/stores/knowledgeGraph";

/**
 * Custom hook for managing authentication context operations
 * @remarks
 * Provides operations to update authentication context including user ID, organization ID,
 * and session ID. Handles both authenticated and unauthenticated users with fallback values.
 * Uses Apollo reactive variables for state management.
 * @returns Authentication operation functions
 */
export const useAuthOperations = () => {
  /**
   * Update authentication context with current user information
   * @param currentUser - Current authenticated user or null/undefined for unauthenticated
   * @remarks
   * Updates user ID, organization ID, and generates new session ID.
   * For authenticated users, uses their actual IDs.
   * For unauthenticated users, uses fallback UUID from AsyncStorage.
   * Clears AsyncStorage when switching from unauthenticated to authenticated.
   * Returns async Promise to support platform-aware storage.
   */
  const updateAuthContext = useCallback(
    async (currentUser: AuthUserFragment | null | undefined) => {
      const previousUserId = userIdVar();
      const newUserId = await getUserId(currentUser);
      const newOrgId = getOrganizationId(currentUser);
      const newSessionId = generateSessionId(newOrgId, newUserId);

      // Only log in non-production with redacted identifiers
      if (env.EXPO_PUBLIC_ENV !== "production") {
        const redactId = (id: string) =>
          id ? `${id.slice(0, 4)}...${id.slice(-4)}` : "";
        console.log("Auth Operations: Updating auth context", {
          authenticated: !!currentUser?.id,
          userIdChanged: previousUserId !== newUserId,
          userIdPrefix: redactId(newUserId),
          orgIdPrefix: redactId(newOrgId),
        });
      }

      currentUserVar(currentUser ?? null);
      userIdVar(newUserId);
      organizationIdVar(newOrgId);
      sessionIdVar(newSessionId);

      // Clear AsyncStorage if user is now authenticated (they don't need the fallback anymore)
      if (currentUser?.id && previousUserId !== newUserId) {
        // Fire-and-forget: Clear AsyncStorage without blocking
        AsyncStorage.removeItem("@gemini-sports-ai:chat-user-id").catch(() => {
          // Ignore storage errors - no logging to avoid exposing identifiers
        });
      }
    },
    []
  );

  return {
    updateAuthContext,
  };
};
